import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clockTime, meridiem } from '../api/attendance'
import { describeClass } from '../api/classDescription'
import { fill, text } from '../api/content'
import {
  refreshConversations,
  createGroup,
  fetchAudiencePeople,
  type AudienceDescriptor,
  type ReplyMode,
} from '../api/conversations'
import { initials } from '../api/people'

type Content = Parameters<typeof text>[0]

/**
 * Making a group (D-618).
 *
 * **Two steps, because the number has to be true.** She picks an audience and
 * a reply mode, then sees exactly who that resolves to and may untick people
 * before she sends. A shared group sends the count *after* unticks, because
 * that is the number the server checks and the number of strangers who will be
 * able to address each other - *the parents of Ballet 2* does not read like
 * thirty-eight and thirty-eight does.
 *
 * **It asks once.** Choosing *Everyone sees it* is the answer; the sentence
 * under it says what that means and how many people it is, and there is no
 * second tick box restating it (D-755).
 *
 * **Private is the default and the safe direction.** A broadcast is the
 * recoverable mistake; a room nobody can close is not.
 *
 * Nobody can be added who the audience did not resolve to: the list is the
 * server's, and this only removes.
 */
export default function NewGroupPanel({
  content,
  audiences,
  startWith,
  onBack,
}: {
  content: Content
  audiences: AudienceDescriptor[]
  /**
   * People already picked on the screen behind this one (D-747).
   *
   * <b>The panel opens on its people step</b> when this is given, because the
   * audience question has already been answered by the act of picking them -
   * asking it again would be the screen forgetting what she just did. Without
   * it the panel behaves as it always has, which is the premade-audience door.
   */
  startWith?: ReadonlySet<string>
  /** Where the people step's Back goes when the picking happened elsewhere. */
  onBack?: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handpicked = audiences.find((a) => a.audience === 'people') ?? null

  /*
   * <b>Only where the server offered the handpicked audience</b>, which is its
   * permission to form a group out of names rather than a class (D-747). The
   * people step renders nothing without a chosen audience, so opening on it
   * with none would be a blank screen; the premade list is the honest thing to
   * show instead. The screen in front of this one keeps her from getting here,
   * and this keeps the panel from having a state it cannot draw.
   */
  const handpicking = startWith !== undefined && startWith.size > 0 && handpicked !== null

  const [chosen, setChosen] = useState<AudienceDescriptor | null>(handpicking ? handpicked : null)
  const [mode, setMode] = useState<ReplyMode>('Private')
  const [step, setStep] = useState<'audience' | 'people'>(handpicking ? 'people' : 'audience')
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [title, setTitle] = useState('')

  /*
   * <b>Picking people one by one</b> (D-726). `asked`: <i>"they should be able to do a
   * custom group not just a premade one"</i>. The list is everyone she may message
   * on her own; it starts with nobody ticked, because unticking thirty to keep
   * three is backwards, and a search narrows it.
   */
  const picking = chosen?.audience === 'people'
  const [picked, setPicked] = useState<Set<string>>(new Set(handpicking ? startWith : []))
  const [search, setSearch] = useState('')
  const label = (a: AudienceDescriptor): string => {
    switch (a.audience) {
      case 'class-parents': {
        /*
         * The day and time are what tell two sections of one style apart:
         * an audit found two ballet classes both reading "Parents of Ballet",
         * distinguished only by a count, with the wrong section one tap away.
         */
        const cls = describeClass(a.label).label
        const when = [a.weekday, a.startsAt ? `${clockTime(a.startsAt)}${meridiem(a.startsAt)}` : null]
          .filter(Boolean)
          .join(' ')
        return fill(content, 'messages.group.audience.classParents', { when, class: cls }) || `Parents of ${when} ${cls}`.replace(/\s+/g, ' ').trim()
      }
      case 'team':
        /*
         * Her own name for the team - *Mini team* - rather than a sentence
         * about it. She named it; a label that rewords it is a second name.
         */
        return a.label
      case 'my-parents':
        return text(content, 'messages.group.audience.myParents', 'All my parents')
      case 'all-teachers':
        return text(content, 'messages.group.audience.allTeachers', 'All teachers')
      case 'all-parents':
        return text(content, 'messages.group.audience.allParents', 'All parents')
      default:
        return a.label
    }
  }

  const count = (n: number) => fill(content, 'messages.group.audience.count', { count: n }) || `${n} people`

  const people = useQuery({
    queryKey: ['conversations', 'audiences', chosen?.audience ?? '', chosen?.key ?? ''],
    queryFn: () => fetchAudiencePeople(chosen!.audience, chosen!.key),
    enabled: step === 'people' && chosen !== null,
  })

  const included = people.data
    ? people.data.people.filter((p) => (picking ? picked.has(p.accountId) : !excluded.has(p.accountId)))
    : []

  const shown = people.data
    ? people.data.people.filter((p) => !picking || p.name.toLowerCase().includes(search.trim().toLowerCase()))
    : []

  /*
   * There was an effect here clearing the confirmation whenever the roster
   * refetched, because a tick was a claim about a number that could move
   * underneath it (D-618). Its comment also said a window focus refetched;
   * that was never true - `main.tsx` sets `refetchOnWindowFocus: false` for
   * every query in the app (D-755, found by the audit).
   *
   * The tick is gone, and with it the thing that could go stale:
   * `confirmShared` is read off the list at the moment she presses, so the
   * number the server is asked to agree with is always the number on screen.
   */

  const start = useMutation({
    mutationFn: () =>
      createGroup({
        audience: chosen!.audience,
        key: chosen!.key,
        title: title.trim(),
        replyMode: mode,
        exclude: picking ? [] : [...excluded],
        include: picking ? [...picked] : undefined,
        confirmShared: mode === 'Shared' ? included.length : null,
      }),
    onSuccess: (view) => {
      refreshConversations(queryClient)
      navigate(`/messages/${view.id}`)
    },
  })

  function choose(a: AudienceDescriptor) {
    setChosen(a)
    setTitle(label(a))
    setExcluded(new Set())
    setPicked(new Set())
    setSearch('')
  }

  function toggle(accountId: string) {
    if (picking) {
      setPicked((was) => {
        const next = new Set(was)
        if (next.has(accountId)) next.delete(accountId)
        else next.add(accountId)
        return next
      })
      return
    }

    setExcluded((was) => {
      const next = new Set(was)
      if (next.has(accountId)) next.delete(accountId)
      else next.add(accountId)
      return next
    })
  }

  const canStart =
    included.length > 0 && title.trim().length > 0 && !start.isPending

  /*
   * <b>One fieldset, asked on whichever step she is on</b> (D-747). A premade
   * audience answers this before the people are known, so it sits on the
   * audience step; a handpicked group never sees that step at all, and an
   * audit found that the question had simply stopped being asked - a shared
   * group of picked names had become unreachable, and the confirmation D-618
   * requires was dead code on that path. Rendered from one place rather than
   * copied, because the safe default and the wording are the whole point of it
   * and two copies would drift on the first change.
   */
  const replyModes = (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-[11px] font-bold uppercase tracking-wider text-ink-2">
        {text(content, 'messages.group.replies', 'When somebody replies')}
      </legend>
      {(['Private', 'Shared'] as const).map((m) => (
        <label
          key={m}
          className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
            mode === m ? 'border-accent' : 'border-line'
          }`}
        >
          <input
            type="radio"
            name="replyMode"
            checked={mode === m}
            onChange={() => setMode(m)}
            className="mt-1 accent-accent"
          />
          <span className="flex flex-col">
            <span className="text-[13.5px] font-semibold">
              {m === 'Private'
                ? text(content, 'messages.group.mode.private', 'Only I see it')
                : text(content, 'messages.group.mode.shared', 'Everyone sees it')}
            </span>
            <span className="text-[11.5px] leading-relaxed text-ink-2">
              {m === 'Private'
                ? text(content, 'messages.group.mode.private.note', "Each reply comes back to you privately. Nobody sees anybody else's answer.")
                : text(content, 'messages.group.mode.shared.note', 'A group conversation. Everybody can read and answer each other.')}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  )

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-line bg-surface px-4 py-4">
      <h3 className="font-display text-lg">{text(content, 'messages.group.new', 'New group')}</h3>

      {step === 'audience' && (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-[11px] font-bold uppercase tracking-wider text-ink-2">
              {text(content, 'messages.group.who', 'Who it goes to')}
            </legend>
            {/*
              <b>Handpicking is not one of the premade audiences any more</b>
              (D-747). It is the screen this panel opens from, so listing it here
              too would be two doors to one thing - and the one that reads as a
              group of its own is the one that is not.
            */}
            {audiences.filter((a) => a.audience !== 'people').map((a) => {
              const id = `${a.audience}:${a.key ?? ''}`
              const picked = chosen !== null && `${chosen.audience}:${chosen.key ?? ''}` === id
              return (
                <label
                  key={id}
                  className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                    picked ? 'border-accent' : 'border-line'
                  }`}
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={picked}
                    onChange={() => choose(a)}
                    className="accent-accent"
                  />
                  <span className="flex min-w-0 grow flex-col">
                    <span className="text-[13.5px] font-semibold wrap-anywhere">{label(a)}</span>
                    <span className="text-[11.5px] text-ink-3">
                      {count(a.people)}
                    </span>
                  </span>
                </label>
              )
            })}
            <p className="text-[11.5px] text-ink-3">
              {text(content, 'messages.group.untick', 'You can untick individual people on the next step.')}
            </p>
          </fieldset>

          {replyModes}

          <button
            type="button"
            disabled={chosen === null}
            onClick={() => setStep('people')}
            className="min-h-[44px] self-end rounded-lg bg-accent px-4 text-[13px] font-semibold text-on-accent disabled:opacity-50"
          >
            {text(content, 'messages.group.next', 'Next')}
          </button>
        </>
      )}

      {step === 'people' && chosen && (
        <>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            {text(content, 'messages.group.title', 'Name')}
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              // A picked group has no audience name to borrow, so the empty box says what it wants.
              placeholder={picking ? 'Name this group' : undefined}
              className="min-h-[44px] rounded-lg border border-line bg-surface px-3 text-[14px] font-normal text-ink"
            />
          </label>

          {/* Asked here on the path that never sees the audience step (D-747). */}
          {handpicking && replyModes}

          {people.isPending && (
            <p className="text-[13px] text-ink-2" role="status">
              {text(content, 'messages.loading', 'Loading…')}
            </p>
          )}

          {people.isError && (
            <p className="text-[13px] text-crit" role="alert">
              {text(content, 'messages.pick.failed', 'Could not load who you can message. Try again in a moment.')}
            </p>
          )}

          {picking && people.isSuccess && (
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              aria-label="Search people to add"
              className="min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 text-[14px]"
            />
          )}

          {people.isSuccess && (
            <ul className="max-h-[22rem] divide-y divide-line overflow-y-auto rounded-xl border border-line">
              {shown.map((p) => (
                <li key={p.accountId}>
                  <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={picking ? picked.has(p.accountId) : !excluded.has(p.accountId)}
                      onChange={() => toggle(p.accountId)}
                      // The label's own text carries the initials as well, so
                      // the name is the accessible one.
                      aria-label={p.name}
                      className="accent-accent"
                    />
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent-2"
                      aria-hidden="true"
                    >
                      {initials(p.name)}
                    </span>
                    <span className="min-w-0 truncate text-[13.5px]">{p.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {people.isSuccess && included.length === 0 && (
            picking ? (
              <p className="text-[13px] text-ink-2">Tick the people to add.</p>
            ) : (
              <p className="text-[13px] text-crit" role="alert">
                {text(content, 'messages.group.nobody', 'Nobody is left in this group.')}
              </p>
            )
          )}

          {/*
            <b>It says what will happen; it does not ask again</b> (D-755).

            There was a <i>Yes, everyone sees it</i> tick box under this
            sentence, and it asked the same question the radio above had
            already been answered with - `asked`: <i>"we don't need to check a
            box twice for the same thing on the same thing."</i> The radio is
            an explicit choice made against a description of exactly this, so
            the box added a step and no information.

            <b>Nothing is weakened by its going.</b> The guard that matters is
            the server's, and it never read this: `confirmShared` carries the
            <i>number</i> of members the client believes it is creating, and a
            shared group is refused if that disagrees with what the server
            resolved. A count the client cannot fake catches the thing a tick
            box cannot - the list having changed underneath her.
          */}
          {mode === 'Shared' && people.isSuccess && included.length > 0 && (
            <p className="rounded-xl bg-warn-bg px-4 py-3 text-[12.5px] leading-relaxed">
              {fill(content, 'messages.group.shared.warning', { count: included.length }) ||
                `Everyone in this group - ${included.length} people - will be able to see each other's names and write to the whole group. This cannot be changed afterwards.`}
            </p>
          )}

          {start.isError && (
            <p className="text-[13px] text-crit" role="alert">
              {start.error.message}
            </p>
          )}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => (onBack ? onBack() : setStep('audience'))}
              className="min-h-[44px] text-[13px] font-semibold text-ink-2"
            >
              {text(content, 'messages.group.back', 'Back')}
            </button>
            <button
              type="button"
              disabled={!canStart}
              onClick={() => start.mutate()}
              className="min-h-[44px] rounded-lg bg-accent px-4 text-[13px] font-semibold text-on-accent disabled:opacity-50"
            >
              {text(content, 'messages.group.start', 'Start group')}
              {people.isSuccess ? ` · ${count(included.length)}` : ''}
            </button>
          </div>
        </>
      )}
    </section>
  )
}
